import { IAdvisorComparisonTableBlock } from "@types";

/**
 * Shared helpers for reading a `comparison_table` block. The backend already formatted every
 * cell; these only *inspect* the strings (never reformat them) so the renderer can decide
 * column alignment, and the chart view can recover the underlying numbers to plot.
 */

/** The backend's placeholder for "no value" — carries no alignment or numeric signal. */
export const DASH = "—";

/**
 * Which columns hold numbers, decided from the cells themselves rather than their position.
 * Column 0 is always the row label. A column counts as numeric only if every cell that says
 * anything at all contains a digit.
 */
export function numericColumns(block: IAdvisorComparisonTableBlock): boolean[] {
  return block.columns.map((_, column) => {
    if (column === 0) return false;
    const values = block.rows
      .map(row => row.cells[column] ?? "")
      .filter(cell => cell !== "" && cell !== DASH);
    return values.length > 0 && values.every(cell => /\d/.test(cell));
  });
}

/**
 * Recover the number behind a formatted money cell ("756,000 PKR" -> 756000), purely so the
 * chart can plot it. Returns null for the dash placeholder or anything without a number. This
 * is the one spot the client reads through the backend's formatting — and only for the chart
 * axis, never the table, which always shows the cell verbatim.
 */
export function parseAmount(cell: string | undefined): number | null {
  if (!cell || cell === DASH) return null;
  const n = Number(cell.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}
